import React, { useEffect, useMemo, useState } from 'react';
import { Check, FileCheck2, Lock } from 'lucide-react';
import { useParams } from 'react-router-dom';
import invoicingService from '../../services/invoicingService';

const formatMoney = (amountCents, currency) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency || 'USD',
    currencyDisplay: 'code',
  }).format((amountCents || 0) / 100);

const formatDate = (value) => {
  if (!value) return 'No expiration date';
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(new Date(`${value}T00:00:00`));
};

const DetailSection = ({ title, children }) => {
  if (!children) return null;
  return (
    <section className="border-t border-slate-200 pt-7">
      <h2 className="mb-3 font-serif text-xl text-slate-950">{title}</h2>
      <div className="whitespace-pre-wrap text-[15px] leading-7 text-slate-600">
        {children}
      </div>
    </section>
  );
};

const ProposalAcceptancePage = () => {
  const { token } = useParams();
  const [proposal, setProposal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({ name: '', email: '', consent: false });

  useEffect(() => {
    let active = true;
    invoicingService
      .getPublicProposal(token)
      .then((result) => active && setProposal(result))
      .catch((requestError) =>
        active && setError(requestError.message || 'This proposal is unavailable.')
      )
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [token]);

  const canAccept = useMemo(
    () =>
      form.name.trim().length > 1 &&
      form.email.includes('@') &&
      form.consent &&
      !submitting,
    [form, submitting]
  );

  const acceptProposal = async (event) => {
    event.preventDefault();
    if (!canAccept) return;
    setSubmitting(true);
    setError('');
    try {
      const accepted = await invoicingService.acceptPublicProposal(token, {
        accepted_by_name: form.name.trim(),
        accepted_by_email: form.email.trim(),
        consent: true,
      });
      setProposal(accepted);
    } catch (requestError) {
      setError(requestError.message || 'We could not record your acceptance.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <main className="grid min-h-screen place-items-center bg-[#f3f1eb] px-6 text-slate-700">
        <p className="animate-pulse text-sm tracking-[0.18em] uppercase">
          Loading proposal
        </p>
      </main>
    );
  }

  if (!proposal) {
    return (
      <main className="grid min-h-screen place-items-center bg-[#f3f1eb] px-6">
        <div className="max-w-md border border-slate-300 bg-white p-8 text-center shadow-sm">
          <FileCheck2 className="mx-auto mb-5 h-9 w-9 text-slate-500" />
          <h1 className="font-serif text-2xl text-slate-950">Proposal unavailable</h1>
          <p className="mt-3 text-sm leading-6 text-slate-600">{error}</p>
        </div>
      </main>
    );
  }

  const accepted = Boolean(proposal.accepted_at);

  return (
    <main className="min-h-screen bg-[#f3f1eb] px-4 py-6 text-slate-950 sm:px-6 sm:py-12">
      <div className="mx-auto max-w-6xl">
        <header className="mb-5 flex items-center justify-between text-xs font-semibold tracking-[0.16em] text-slate-500 uppercase">
          <span>Rely agreements</span>
          <span className="flex items-center gap-2 normal-case tracking-normal">
            <Lock className="h-3.5 w-3.5" /> Secure link
          </span>
        </header>

        <div className="grid overflow-hidden border border-slate-300 bg-white shadow-[0_28px_80px_rgba(15,23,42,0.12)] lg:grid-cols-[minmax(0,1fr)_360px]">
          <article className="px-6 py-8 sm:px-10 sm:py-12 lg:px-14">
            <div className="mb-12 flex flex-col gap-8 border-b border-slate-900 pb-9 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <p className="mb-3 text-xs font-bold tracking-[0.2em] text-emerald-700 uppercase">
                  {proposal.company_name}
                </p>
                <h1 className="max-w-2xl font-serif text-4xl leading-tight tracking-[-0.02em] text-slate-950 sm:text-5xl">
                  {proposal.document_title}
                </h1>
              </div>
              <div className="shrink-0 text-sm leading-6 text-slate-500 sm:text-right">
                <p className="font-semibold text-slate-900">{proposal.proposal_number}</p>
                <p>Issued {formatDate(proposal.issue_date)}</p>
                <p>Valid through {formatDate(proposal.valid_until)}</p>
              </div>
            </div>

            <div className="mb-10 grid gap-6 sm:grid-cols-2">
              <div>
                <p className="text-xs font-semibold tracking-wider text-slate-400 uppercase">Prepared for</p>
                <p className="mt-2 text-lg font-semibold">{proposal.customer_name}</p>
              </div>
              <div className="sm:text-right">
                <p className="text-xs font-semibold tracking-wider text-slate-400 uppercase">Agreement total</p>
                <p className="mt-2 font-serif text-3xl">{formatMoney(proposal.total_amount_cents, proposal.currency)}</p>
              </div>
            </div>

            <div className="space-y-8">
              <DetailSection title="Introduction">{proposal.introduction}</DetailSection>
              <DetailSection title="Scope">{proposal.scope}</DetailSection>
              {proposal.deliverables?.length ? (
                <DetailSection title="Deliverables">
                  <ul className="space-y-2">
                    {proposal.deliverables.map((item) => (
                      <li key={item} className="flex gap-3"><span className="text-emerald-600">—</span>{item}</li>
                    ))}
                  </ul>
                </DetailSection>
              ) : null}

              <section className="border-t border-slate-200 pt-7">
                <h2 className="mb-5 font-serif text-xl">Commercial terms</h2>
                <div className="overflow-hidden border border-slate-200">
                  {proposal.line_items.map((item) => (
                    <div key={`${item.description}-${item.amount_cents}`} className="grid grid-cols-[1fr_auto] gap-4 border-b border-slate-200 px-4 py-4 last:border-b-0">
                      <div>
                        <p className="font-medium text-slate-900">{item.description}</p>
                        <p className="mt-1 text-xs text-slate-500">Qty. {item.quantity}</p>
                      </div>
                      <p className="font-semibold tabular-nums">{formatMoney(item.amount_cents, proposal.currency)}</p>
                    </div>
                  ))}
                </div>
              </section>

              <DetailSection title="Timeline">{proposal.timeline}</DetailSection>
              <DetailSection title="Terms">{proposal.terms}</DetailSection>
              <DetailSection title="Acceptance terms">{proposal.acceptance_terms}</DetailSection>
            </div>
          </article>

          <aside className="border-t border-slate-300 bg-slate-950 px-6 py-8 text-white sm:px-8 lg:border-t-0 lg:border-l lg:py-12">
            <div className="lg:sticky lg:top-8">
              {accepted ? (
                <div role="status">
                  <div className="mb-7 grid h-12 w-12 place-items-center rounded-full bg-[#71de80] text-slate-950">
                    <Check className="h-6 w-6" strokeWidth={3} />
                  </div>
                  <h2 className="font-serif text-3xl">Proposal accepted</h2>
                  <p className="mt-4 text-sm leading-6 text-slate-300">
                    Accepted by {proposal.accepted_by_name} on{' '}
                    {new Intl.DateTimeFormat('en-US', { dateStyle: 'long', timeStyle: 'short' }).format(new Date(proposal.accepted_at))}.
                  </p>
                </div>
              ) : (
                <form onSubmit={acceptProposal}>
                  <p className="text-xs font-semibold tracking-[0.18em] text-[#71de80] uppercase">Acceptance</p>
                  <h2 className="mt-3 font-serif text-3xl leading-tight">Confirm this agreement</h2>
                  <p className="mt-4 text-sm leading-6 text-slate-300">Your identity, timestamp, and this document’s fingerprint will be preserved as an acceptance record.</p>

                  <label className="mt-8 block text-sm font-medium" htmlFor="acceptance-name">Full name</label>
                  <input id="acceptance-name" value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} className="mt-2 min-h-12 w-full border border-slate-600 bg-slate-900 px-3 text-white outline-none transition focus:border-[#71de80]" autoComplete="name" />

                  <label className="mt-5 block text-sm font-medium" htmlFor="acceptance-email">Email</label>
                  <input id="acceptance-email" type="email" value={form.email} onChange={(event) => setForm({ ...form, email: event.target.value })} className="mt-2 min-h-12 w-full border border-slate-600 bg-slate-900 px-3 text-white outline-none transition focus:border-[#71de80]" autoComplete="email" />

                  <label className="mt-6 flex cursor-pointer items-start gap-3 text-sm leading-6 text-slate-300">
                    <input type="checkbox" checked={form.consent} onChange={(event) => setForm({ ...form, consent: event.target.checked })} className="mt-1 h-4 w-4 accent-[#71de80]" />
                    <span>{proposal.consent_text}</span>
                  </label>

                  {error ? <p role="alert" className="mt-5 text-sm text-rose-300">{error}</p> : null}

                  <button type="submit" disabled={!canAccept} className="mt-7 min-h-12 w-full bg-[#71de80] px-5 font-bold text-slate-950 transition hover:bg-[#8be696] disabled:cursor-not-allowed disabled:bg-slate-700 disabled:text-slate-400">
                    {submitting ? 'Recording acceptance…' : 'Accept proposal'}
                  </button>
                  <p className="mt-5 break-all text-[11px] leading-5 text-slate-500">Document fingerprint: {proposal.document_hash}</p>
                </form>
              )}
            </div>
          </aside>
        </div>
      </div>
    </main>
  );
};

export default ProposalAcceptancePage;
